import { Observable } from 'rxjs';
import { BaseController } from '~client/controller/base-controller';
import { AccessControlReportResponse } from '~client/model/access-control-report-response';
import { ReportingMarketplaceSalesResponse } from '~client/model/reporting-marketplace-sales-response';
import { OfferDemographicsRequest } from '~client/model/offer-demographics-request';
import { ReportingEventDetailResponse } from '~client/model/reporting-event-detail-response';
import { ReportingMarketplaceDetailResponse } from '~client/model/reporting-marketplace-detail-response';
import { TokenHolderDemographicResponse } from '~client/model/token-holder-demographic-response';
import { UserDemographicResponse } from '~client/model/user-demographic-response';
import { ReportingEventListResponse } from '~client/model/reporting-event-list-response';
import { MarketplaceSalesOverTimeRequest } from '~client/model/marketplace-sales-over-time-request';
import { MarketplaceSalesOverTimeResponse } from '~client/model/marketplace-sales-over-time-response';

/* Reporting Functions for the Client Dashboard */
export class ReportingController extends BaseController {
  /* Function for the Client Dashboard to retrieve past and future event information */
  public getEvents$(
    completedEvents: boolean,
    previousPageKey: string,
  ): Observable<ReportingEventListResponse> {
    return this.get$('/reporting/eventList', { completedEvents, previousPageKey });
  }

  /* Function for the Client Dashboard to retrieve event details */
  public getEventDetails$(eventId: string): Observable<ReportingEventDetailResponse> {
    return this.get$('/reporting/eventDetails', { eventId });
  }

  /* Function for the Client Dashboard to retrieve marketplace details. */
  public getMarketplaceDetails$(
    marketplaceId: number,
    salesGroupId?: number,
  ): Observable<ReportingMarketplaceDetailResponse> {
    return this.get$('/reporting/marketplaceDetails', { marketplaceId, salesGroupId });
  }

  public getMarketplaceSalesForCategory$(
    marketplaceId: number,
    salesGroupId?: number,
  ): Observable<ReportingMarketplaceSalesResponse> {
    return this.get$('/reporting/marketplaceSales/category', { marketplaceId, salesGroupId });
  }

  public getMarketplaceSalesForSection$(
    categoryId: number,
    marketplaceId: number,
    salesGroupId?: number,
  ): Observable<ReportingMarketplaceSalesResponse> {
    return this.get$('/reporting/marketplaceSales/section', {
      categoryId,
      marketplaceId,
      salesGroupId,
    });
  }

  public getOfferDemographics$(
    request: OfferDemographicsRequest,
  ): Observable<UserDemographicResponse> {
    return this.post$<UserDemographicResponse>('/reporting/offerDemographics', request);
  }

  public getTokenHolderDemographics$(
    eventId: number,
    salesGroupId?: number,
  ): Observable<TokenHolderDemographicResponse> {
    return this.get$('/reporting/tokenHolderDemographics', { eventId, salesGroupId });
  }

  /* Gets excel report for event summary. */
  public getEventSummary$(eventId: number): Observable<Blob> {
    return this.get$<Blob>(
      '/reporting/generateEventSummary',
      { eventId },
      { responseType: 'blob' },
    );
  }

  /* Gets excel report with list of attendees. */
  public getAttendeeList$(eventId: number): Observable<Blob> {
    return this.get$<Blob>(
      '/reporting/generateAttendeeList',
      { eventId },
      { responseType: 'blob' },
    );
  }

  /* Gets excel report with list of transactions. */
  public getTransactionList$(eventId: number): Observable<Blob> {
    return this.get$<Blob>(
      '/reporting/generateTransactionList',
      { eventId },
      { responseType: 'blob' },
    );
  }

  public getSalesOverTime$(
    request: MarketplaceSalesOverTimeRequest,
  ): Observable<MarketplaceSalesOverTimeResponse> {
    return this.post$('/reporting/marketplaceSales/overTime', request);
  }

  public getAccessControlDetails$(eventId: number): Observable<AccessControlReportResponse> {
    return this.get$('/reporting/accessControl', { eventId });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/client/src/controller/reporting-controller.ts